/**
 * The app updates itself (1.6.5 §2, D-1.6.5-2: the person never has to know a new version exists). Two sources
 * say that the site has a newer version:
 *
 *  - /version.json (1.5.13 §1c.5, lib/version.ts): asked when the app opens, every few minutes, on every
 *    navigation, and every time the app comes to the front (visible again, pageshow, focus) — throttled, so a
 *    burst is one request. It does not depend on the service worker (iOS drops registrations).
 *  - the service worker (vite-plugin-pwa, autoUpdate; lib/sw-register.ts): it fetches the new version in the
 *    background, takes over, and says when it is in place.
 *
 * Either way the page then **reloads once by itself** when nothing would be lost — no sheet open, no form dirty,
 * nothing typed on the page, and it has been so for a moment (lib/app-update.ts; never a loop). Only otherwise does
 * the banner show — "A new version is ready — Reload", one banner, never two — and as soon as the sheet is closed
 * the page reloads by itself after all. Reload says what it is doing ("Updating to 1.6.5…") and cannot leave the
 * old version in place silently. The other exception is a chunk that no longer exists: lib/chunk-reload.ts.
 *
 * The banner is announced politely (role="status"), its buttons are ordinary buttons in the tab order, it sits
 * above the phone's tab bar and below any open sheet, and while it shows the page keeps room for it at the bottom
 * (html[data-update-banner], styles/app.css) so it covers no control.
 *
 * `UpdateLine` is the same update as a line in Settings (My settings, Admin → About): "App 1.6.4 · the site has
 * 1.6.5 · Update now", or "Up to date".
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/Icons';
import { updaterFor } from '@/lib/app-update';
import { clearTyped, watchTyping } from '@/lib/dirty';
import { VERSION_FRONT_GAP_MS, VERSION_POLL_MS, versionWatch, type VersionWatch } from '@/lib/version';

/** "Later" puts the banner away for this long; a tab left on an old version is asked again after it. */
const LATER_MS = 30 * 60 * 1000;
/** How often a page with an update waiting looks whether it is safe; two looks in a row must say yes. */
const SAFE_TICK_MS = 750;

/** The watch and its updater as React state (the app's own unless a test hands one in). */
function useUpdate(watch: VersionWatch | undefined) {
  const w = watch ?? versionWatch;
  const u = updaterFor(w);
  const stale = useSyncExternalStore(w.subscribe, w.isStale, () => false);
  const latest = useSyncExternalStore(w.subscribe, w.latest, () => null);
  const updating = useSyncExternalStore(u.subscribe, u.state, () => 'idle' as const) === 'updating';
  // the app's own watch runs in a build only (dev and unit tests have no /version.json); a test hands in its own
  const on = !!watch || !import.meta.env.DEV;
  return { w, u, on, stale, updating, latest, target: latest && stale ? w.target() : null };
}

export function PwaUpdate({ watch, tickMs = SAFE_TICK_MS }: { watch?: VersionWatch; tickMs?: number }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { w, u, on, stale, updating, target } = useUpdate(watch);
  const [later, setLater] = useState(false);
  /** The page could not simply reload by itself (something is open, or it already tried): the banner asks. */
  const [ask, setAsk] = useState(false);

  // the service worker: registered once; when its new version is in place, the same path — and the site is
  // asked which version that is
  useEffect(() => {
    if (import.meta.env.DEV || !('serviceWorker' in navigator)) return undefined;
    let cancelled = false;
    let stop: (() => void) | undefined;
    void import('@/lib/sw-register')
      .then(({ registerServiceWorker }) =>
        registerServiceWorker(() => {
          w.markStale();
          void w.check({ minGapMs: 0 });
        }),
      )
      .then((h) => {
        if (cancelled) h?.stop();
        else stop = h?.stop;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [w]);

  // every few minutes, and every time the app comes to the front
  useEffect(() => {
    if (!on) return undefined;
    const timer = window.setInterval(() => void w.check(), VERSION_POLL_MS);
    const front = () => void w.check({ minGapMs: VERSION_FRONT_GAP_MS });
    const seen = () => {
      if (document.visibilityState === 'visible') front();
    };
    document.addEventListener('visibilitychange', seen);
    window.addEventListener('pageshow', front);
    window.addEventListener('focus', front);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', seen);
      window.removeEventListener('pageshow', front);
      window.removeEventListener('focus', front);
    };
  }, [on, w]);

  // when the app opens and on every navigation (the watch keeps a burst to one request); what was typed on the
  // page the person just left went with it
  useEffect(() => {
    clearTyped();
    if (on) void w.check();
  }, [on, w, location.pathname]);

  useEffect(() => (on ? watchTyping() : undefined), [on]);

  // a newer version is live: reload by itself once it is safe — and has been for a moment, so a sheet that has
  // just closed has had time to send what it had to send. Not safe, or already tried: the banner asks.
  useEffect(() => {
    if (!on || !stale) return undefined;
    let calm = 0;
    const look = () => {
      if (u.state() === 'updating') return;
      if (!u.canAuto() || !u.safe()) {
        calm = 0;
        setAsk(true);
        return;
      }
      calm += 1;
      if (calm < 2) return;
      calm = 0;
      void u.auto().then((leaving) => {
        if (!leaving) setAsk(true);
      });
    };
    look();
    const timer = window.setInterval(look, tickMs);
    return () => window.clearInterval(timer);
  }, [on, stale, u, tickMs]);

  useEffect(() => {
    if (!later) return undefined;
    const timer = window.setTimeout(() => setLater(false), LATER_MS);
    return () => window.clearTimeout(timer);
  }, [later]);

  const show = stale && (updating || (ask && !later));
  useEffect(() => {
    if (!show) return undefined;
    document.documentElement.setAttribute('data-update-banner', 'true');
    return () => document.documentElement.removeAttribute('data-update-banner');
  }, [show]);

  if (!show) return null;
  const words = updating ? (target ? t('update165.updatingTo', { version: target.version }) : t('update165.updating')) : u.stuck() ? t('update165.stuck', { app: w.running.version }) : t('pwa.newVersion');
  return (
    <div className="pwa-update" role="status" aria-live="polite" data-testid="pwa-update" data-updating={updating ? 'true' : undefined}>
      <Icon name="refresh" />
      <span>{words}</span>
      <Button size="sm" onClick={() => u.press()} disabled={updating} data-testid="pwa-reload">
        {t('pwa.reload')}
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setLater(true)} disabled={updating}>
        {t('pwa.later')}
      </Button>
    </div>
  );
}

/**
 * Settings: is this device on the version the site has? "Update now" is the banner's Reload. The words are a
 * status, so a screen reader hears "Updating to 1.6.5…" when the button is pressed.
 */
export function UpdateLine({ watch }: { watch?: VersionWatch }) {
  const { t } = useTranslation();
  const { w, u, on, stale, updating, latest, target } = useUpdate(watch);
  useEffect(() => {
    if (on) void w.check({ minGapMs: VERSION_FRONT_GAP_MS });
  }, [on, w]);
  const app = w.running.version;
  const words = updating
    ? target
      ? t('update165.updatingTo', { version: target.version })
      : t('update165.updating')
    : stale
      ? target && target.version !== app
        ? t('update165.differs', { app, site: target.version })
        : t('update165.newerBuild', { app })
      : latest
        ? t('update165.upToDate', { app })
        : t('update165.appOnly', { app });
  return (
    <span className="update-line" data-testid="update-line">
      <span role="status">{words}</span>
      {stale ? (
        <Button size="sm" variant="secondary" onClick={() => u.press()} disabled={updating} data-testid="update-now">
          {t('update165.updateNow')}
        </Button>
      ) : null}
    </span>
  );
}
